import { useState } from 'react';
import { Alert, Flex, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import globalNotification from '../../common/hooks/globalNotification';
import { notificationType } from '../../common/constants/notificationType';
import { useVinExtractionService } from '../../services/vinExtractionService';
import { isProcessingOn, parseSettings } from '../../utils/vinExtraction';
import { spacing } from '../../theming';
import { labelStyle } from './vinExtractionStyles';
import { notifyVinError } from './vinExtractionErrors';
import ProcessingSwitchControl, { HELP_ID, LABEL_ID } from './ProcessingSwitchControl';

/** Confirm → set the new state; the switch then shows what the API answered, or what a re-read says. */
const useProcessingToggle = ({ on, onSettingsChange, onReload }) => {
    const { t } = useTranslation('vinExtraction');
    const { setProcessing } = useVinExtractionService();
    const [busy, setBusy] = useState(false);

    const toggle = async () => {
        setBusy(true);
        try {
            const next = parseSettings(await setProcessing(!on));
            // the routes return the settings body; anything else is re-read rather than trusted
            if (next) onSettingsChange(next);
            else await onReload();
            globalNotification.open({
                type: notificationType.SUCCESS,
                message: t(on ? 'edit.processingPaused' : 'edit.processingStarted'),
                placement: 'top',
            });
        } catch (error) {
            notifyVinError(t, error);
            // e.g. the env switched off since the page loaded: re-read so the switch shows the truth
            await onReload();
        } finally {
            setBusy(false);
        }
    };

    return { busy, toggle };
};

/**
 * The one Processing switch (batch job only; polling has no control here). Applied on confirm, not on Save;
 * locked while the environment kill switch is off (E2), and in a read-only session (E27).
 */
export default function ProcessingSwitch({ settings, readOnly, onSettingsChange, onReload }) {
    const { t } = useTranslation('vinExtraction');
    const on = isProcessingOn(settings);
    const envOff = settings.enabledByEnv === false;
    const { busy, toggle } = useProcessingToggle({ on, onSettingsChange, onReload });

    return (
        <Flex vertical gap={spacing[1]}>
            <Flex align="center" justify="space-between" gap={spacing[2]}>
                <Typography.Text strong id={LABEL_ID}>
                    {t('edit.processing')}
                </Typography.Text>
                <ProcessingSwitchControl
                    on={on}
                    busy={busy}
                    locked={envOff || readOnly}
                    envOff={envOff}
                    onConfirm={toggle}
                />
            </Flex>
            <span id={HELP_ID} style={labelStyle}>
                {t('edit.processingHelp')}
            </span>
            {envOff && <Alert type="warning" showIcon message={t('edit.disabledByEnv')} />}
        </Flex>
    );
}
